import { NitexEditor } from '@nitex/nitex-design-tool';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import '@nitex/nitex-design-tool/dist/index.css';

import { toastError, toastInfo, toastSuccess } from '@components/Toast';

import BlockSuggestion from '@features/blockSuggestion/components/block/BlockSuggestion';
import FileUploads from '@features/blockSuggestion/components/fileUpload/FileUploads';
import TopHeader from '@features/design/components/TopHeader';

import {
	deleteProductDocument,
	fetchDesignToolComment,
	fetchProductInfo,
	uploadProductDocument
} from '@store/actions/DesignToolAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { instance } from '@services/axiosTimeTrackingSerivce';

import ApiClient from '@lib/apiClient';

import { checkIsValidImageAndSize, designToolRequiredTools, getUserInfo } from '@utils/Util';

import { BASE_URL } from '@/constants/constant';

let defaultArtBoardImages = {};
const TABS_NAME = ['block', 'files'];

const ALLOWED_IMAGES_TYPE = [
	'image/jpeg',
	'image/jpg',
	'image/png',
	'image/webp',
	'image/gif',
	'image/bmp',
	'image/svg+xml',
	'image/tiff',
	'image/vnd.microsoft.icon',
	'image/vnd.wap.wbmp',
	'image/x-icon',
	'image/x-ico',
	'image/x-portable-bitmap',
	'image/x-portable-graymap',
	'image/x-portable-pixmap',
	'image/x-xbitmap',
	'image/x-xpixmap',
	'image/x-xwindowdump'
];

const DesignTool = ({
	loader,
	setLoader,
	activeTab,
	openCommentPanel,
	setOpenCommentPanel,
	setIsDesignSaved,
	stompClient,
	onChangeActiveTab
}) => {
	const [reload, setReload] = useState(true);
	const [documentList, setDocumentList] = useState([]);
	const [pinnedImage, setPinnedImage] = useState([]);
	const [, setAttachmentList] = useState([]);
	const [, setArtBoardImages] = useState({});
	const [artBoardData, setArtBoardData] = useState({});
	const [counterObj, setCounterObj] = useState(false);
	const [, setActiveToolName] = useState('');
	const designToolStore = useSelector((store) => store.designToolStore);
	const designToolCommentStore = useSelector((store) => store.designToolCommentStore);
	const [commentNumber, setCommentNumber] = useState(null);
	const artBoardRef = useRef([]);
	const exportCanvasData = useRef(null);
	const timer = useRef(0);
	const [disabled, setDisabled] = useState(true);
	const [isErrorOccurred, setIsErrorOccurred] = useState(true);
	const [timerCount, setTimerCount] = useState(0);
	const [selectedTool, setSelectedTool] = useState('');
	const [showGuideLine, setShowGuideLine] = useState(true);
	const dispatch = useDispatch();
	const { id } = useParams();
	const productRef = useRef(null);
	const [convertedImage, setConvertedImage] = useState(null);
	const navigate = useNavigate();
	const location = useLocation();

	useEffect(() => {
		const designToolsActiveTab = new URLSearchParams(location.search).get('designToolsActiveTab');
		if (designToolsActiveTab) {
			setSelectedTool(designToolsActiveTab);
		}
	}, [location]);

	useEffect(() => {
		ApiClient.GET(`${BASE_URL}/product/${id}/collections-document`, '', true)
			.then(({ data }) => {
				setPinnedImage(data.pinnedList);
			})
			.catch(({ response }) => {
				setLoader(false);
				toastError(response.data.message);
			});
	}, []);

	useEffect(() => {
		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [disabled]);

	const handleBeforeUnload = async (e) => {
		e.preventDefault();
		if (!disabled) {
			await saveAllCanvasData(false);
			const message = 'Are you sure you want to leave? All provided data will be lost.';
			e.returnValue = message;
			return message;
		}
	};

	const generateArtBoardNumber = (productId) => {
		ApiClient.GET('generateArtBoardNumber', productId)
			.then((response) => {
				setCommentNumber(response.data);
			})
			.catch((err) => {
				toastError(err.response.data.message);
			});
	};

	useEffect(() => {
		defaultArtBoardImages = {};
		productRef.current = id;
		artBoardRef.current = [];
		setArtBoardData({});
		setArtBoardImages({});
		setCounterObj(false);
		setLoader(true);
		setReload(true);
		// dispatch(fetchDesignToolComment(id));
		generateArtBoardNumber(id);
		const params = `${id}?checkBuyerSelectionStatus=true`;
		dispatch(fetchProductInfo(params));
		fetchArtBoard(id);
	}, [id, convertedImage]);

	useEffect(() => {
		if (designToolStore?.allImageDocuments) {
			setDocumentList(designToolStore?.allImageDocuments);
		}

		if (designToolStore?.allNotImageDocuments) {
			setAttachmentList(designToolStore?.allNotImageDocuments);
		}
	}, [designToolStore]);

	const saveAllCanvasData = (bool = true, loader) => {
		if (exportCanvasData.current) {
			try {
				// Temporarily disable guideline before export
				setShowGuideLine(false);
				setTimeout(() => {
					let canvasExportedData = exportCanvasData.current?.exportCanvas(bool);
					onCanvasChange(canvasExportedData, {}, loader);
					// Re-enable guideline after save
					setTimeout(() => setShowGuideLine(true), 100);
				}, 100);
			} catch {
				setShowGuideLine(true);
				/* Empty Block */
			}
		}
	};

	useEffect(() => {
		return () => {
			if (artBoardRef.current.length > 0) {
				saveAllCanvasData(false, true);
			} else {
				saveAllCanvasData(true, true);
			}
		};
	}, []);

	const generateArtBoard = async () => {
		await ApiClient.GET(`${BASE_URL}/art-board/product/${id}/canvas`, '', true)
			.then((response) => {
				if (Object.keys(response.data).length > 0) {
					defaultArtBoardImages = response.data;
					setArtBoardImages(response.data);
					setTimeout(() => {
						setLoader(false);
						setReload(false);
					}, 3000);
				} else {
					setLoader(false);
					setReload(false);
				}
			})
			.catch(() => {
				setLoader(false);
				setReload(false);
				toastError('Failed to fetch canvas draft images!');
			});
	};

	const generateArtBoardData = (url) => {
		return new Promise((resolve, reject) => {
			let configs = { headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
			instance
				.get(url, configs)
				.then((response) => {
					resolve(response.data);
				})
				.catch((error) => {
					reject(error);
				});
		});
	};

	const onPlaceImageOnArtboard = async (file) => {
		dispatch(openLoader());
		if (file?.documentResponse?.name.includes('.pdf')) {
			try {
				const { data } = await ApiClient.POST(
					`${BASE_URL}/entity-file/${file?.documentResponse?.id}/product/${id}/convert-to-image`,
					file,
					'',
					true
				);
				setConvertedImage(data);
			} catch (response) {
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Something went wrong! Please try again.');
				}
			} finally {
				dispatch(closeLoader());
			}
		}
	};

	const fetchArtBoard = async (productId) => {
		await ApiClient.GET('fetchArtBoard', productId)
			.then((response) => {
				let listOfBoards = response.data;
				if (listOfBoards.length > 0) {
					let listOfBord = [];
					let listOfArtBoardData = {};
					let count = 0;
					for (let item of listOfBoards) {
						generateArtBoardData(item.historyDocUrl)
							.then((res) => {
								count++;
								let artBoardObj = res;
								artBoardObj.id = item.id;
								artBoardObj.serial = item.serial;
								listOfArtBoardData[item.code] = artBoardObj;
								setCounterObj(parseInt(listOfBoards.length) === count);
							})
							.catch(() => {
								count++;
								toastError('Something went wrong!');
								if (parseInt(listOfBoards.length) === count) {
									setReload(false);
									setLoader(false);
								}
							});
						listOfBord.push({
							id: item.id,
							name: item.name,
							code: item.code,
							serial: item.serial
						});
					}
					artBoardRef.current = listOfBord;
					setArtBoardData(listOfArtBoardData);
				} else {
					generateArtBoard();
				}
			})
			.catch(() => {
				setLoader(false);
				toastError('Failed to fetch art board data!');
			});
	};

	useEffect(() => {
		if (counterObj === true) {
			setReload(false);
			setLoader(false);
		}
	}, [counterObj]);

	const isArtBoardExist = (code) => {
		let selectedBoard = undefined;
		const arrayUniqueByKey = [
			...new Map(artBoardRef.current.map((item) => [item['code'], item])).values()
		];
		for (let item of arrayUniqueByKey) {
			if (item.code.toString() === code.toString()) {
				selectedBoard = item;
				break;
			}
		}
		return selectedBoard;
	};

	const removeDocument = (document) => {
		dispatch(deleteProductDocument(id, document.id));
	};

	const uploadProduct = async (files) => {
		if (checkIsValidImageAndSize(files)) {
			setLoader(true);
			let allFileList = [];
			await files.forEach((file) => {
				if (file && ALLOWED_IMAGES_TYPE.includes(file.type)) {
					let data = {
						name: file?.name,
						docMimeType: file?.type === '' ? 'application/octet-stream' : file?.type,
						documentType: file?.fileType,
						base64Str: ''
					};
					let reader = new FileReader();
					reader.readAsDataURL(file);
					reader.onload = () => {
						data.base64Str = reader.result;
						allFileList.push(data);
					};
					reader.onerror = function (error) {
						toastError(error);
					};
				} else {
					setLoader(false);
					toastInfo('Please upload your file on the files section.');
				}
			});
			setTimeout(() => {
				dispatch(uploadProductDocument(id, allFileList, setLoader));
			}, 100);
		}
	};

	const changeCanvasModificationFlag = (canvasId) => {
		if (exportCanvasData.current !== null) {
			exportCanvasData.current.toggleCanvasModification(canvasId);
			setDisabled(false);
		}
	};

	const storeArtBoard = async (params, postDataObj) => {
		await ApiClient.POST('storeArtBoard', params)
			.then((response) => {
				artBoardRef.current = [...artBoardRef.current, response.data.payload];
				if (postDataObj && postDataObj.artBoardId === params.code) {
					let dataSet = { ...postDataObj };
					dataSet['artBoardId'] = response.data.payload.id;
					savePost(dataSet);
				}
			})
			.catch(() => {
				setIsErrorOccurred(true);
				changeCanvasModificationFlag(params.code);
				toastError('Failed to store art board!');
			});
	};

	const updateArtBoard = async (params, artBoardId, postDataObj) => {
		await ApiClient.PUT('updateArtBoard', params, artBoardId)
			.then(() => {
				if (postDataObj && postDataObj.artBoardId === params.code) {
					let dataSet = { ...postDataObj };
					dataSet['artBoardId'] = artBoardId;
					savePost(dataSet);
				}
			})
			.catch(() => {
				setIsErrorOccurred(true);
				changeCanvasModificationFlag(params.code);
				toastError('Failed to update art board!');
			});
	};

	const generateCanvasPostData = (singleItem, value) => {
		let item = value[singleItem];

		// Create a copy of canvasData and remove guideline-related properties
		let canvasDataWithoutGuidelines = { ...item.canvasData };

		// Remove guideline properties from the canvas data
		delete canvasDataWithoutGuidelines.guidelines;
		delete canvasDataWithoutGuidelines.showGuideLine;
		delete canvasDataWithoutGuidelines.guideLines;

		// Filter out guideline objects from the objects array (if exists)
		if (canvasDataWithoutGuidelines.objects && Array.isArray(canvasDataWithoutGuidelines.objects)) {
			canvasDataWithoutGuidelines.objects = canvasDataWithoutGuidelines.objects.filter(
				(obj) => obj.type !== 'guideline' && obj.type !== 'guide' && !obj.isGuideline
			);
		}

		return {
			code: item.canvasId,
			documentDTO: {
				base64Str: item.canvasString,
				docMimeType: 'image/jpeg',
				documentType: 'REFERENCE_IMAGE',
				name: item.canvasName + '.jpg'
			},
			historyJson: JSON.stringify({
				...canvasDataWithoutGuidelines,
				canvasHistory: { index: null, states: [] }
			}),
			name: item.canvasName,
			productId: productRef.current,
			serial: item.serial
		};
	};

	const onCanvasChange = (value = {}, postDataObj, loader = false, handleCanvasReArrange) => {
		if (Object.keys(value).length > 0) {
			if (loader === true) {
				setIsDesignSaved(false);
				setLoader(true);
			}
			let count = 0;
			setIsErrorOccurred(false);
			for (let singleItem in value) {
				let item = value[singleItem];
				let postData = generateCanvasPostData(singleItem, value);
				let selectedArtBoard = isArtBoardExist(item.canvasId);
				if (selectedArtBoard) {
					updateArtBoard(postData, selectedArtBoard.id, postDataObj).finally(() => {
						count++;
						if (Object.keys(value).length === count) {
							setLoader(false);
							if (handleCanvasReArrange) handleCanvasReArrange();
							setIsDesignSaved(true);
							timer.current = 0;
							setTimerCount(0);
							setDisabled(true);
						}
					});
				} else {
					storeArtBoard(postData, postDataObj).finally(() => {
						count++;
						if (Object.keys(value).length === count) {
							setLoader(false);
							if (handleCanvasReArrange) handleCanvasReArrange();
							setIsDesignSaved(true);
							timer.current = 0;
							setTimerCount(0);
							setDisabled(true);
						}
					});
				}
			}
		}
	};

	const updateCanvasSerial = async (data) => {
		try {
			setLoader(true);
			const artBoardList = await ApiClient.GET('fetchArtBoard', id);
			let postData = [];
			for (let canvas of data) {
				let selectedArtBoard = artBoardList?.data?.find((item) => item.code === canvas.canvasId);
				if (selectedArtBoard && canvas.newSerialId !== null) {
					postData.push({
						id: selectedArtBoard.id,
						serial: canvas.newSerialId
					});
				}
			}
			await ApiClient.POST('updateArtBoardSerial', postData, id);
			await setDisabled(true);
			await setLoader(false);
			navigate(0);
		} catch {
			setLoader(false);
			toastError('Unable to rearrange art boards!');
		}
	};

	const reArrangeCanvasHandler = async (data) => {
		try {
			if (exportCanvasData.current) {
				try {
					// Temporarily disable guideline before export
					setShowGuideLine(false);
					await new Promise((resolve) => setTimeout(resolve, 100));

					let canvasExportedData = exportCanvasData.current?.exportCanvas(false);
					if (Object.keys(canvasExportedData).length > 0) {
						onCanvasChange(canvasExportedData, {}, loader, () => {
							updateCanvasSerial(data);
							// Re-enable guideline after save
							setTimeout(() => setShowGuideLine(true), 100);
						});
					} else {
						await updateCanvasSerial(data);
						setShowGuideLine(true);
					}
				} catch {
					setShowGuideLine(true);
					/* Empty Block */
				}
			}
		} catch {
			setShowGuideLine(true);
			setLoader(false);
			toastError('Unable to rearrange art boards!');
		}
	};

	const removeArtBoard = async (artBoardCode) => {
		let selectedArtBoard = isArtBoardExist(artBoardCode);
		if (selectedArtBoard !== undefined) {
			await ApiClient.DELETE('updateArtBoard', '', selectedArtBoard.id).catch((err) => {
				toastError(err.response.data.message);
			});
		}
	};

	const addNewPost = (value) => {
		setLoader(true);
		let postData = { ...value };
		postData.productId = id;
		postData.postType = 'PRODUCT_DEVELOPMENT_COMMENT';
		postData.postTag = 'PRODUCT_DEVELOPMENT_COMMENT';
		if (exportCanvasData.current) {
			try {
				// Temporarily disable guideline before export
				setShowGuideLine(false);
				setTimeout(() => {
					let canvasExportedData = exportCanvasData.current?.exportCanvas(true);
					onCanvasChange(canvasExportedData, postData);
					// Re-enable guideline after save
					setTimeout(() => setShowGuideLine(true), 100);
				}, 100);
			} catch {
				setShowGuideLine(true);
				setLoader(false);
			}
		} else {
			setLoader(false);
		}
	};

	const savePost = (postData) => {
		setLoader(true);
		let url;
		if (postData.postId) {
			url = 'addPostReply';
		} else {
			url = 'addNewPost';
			postData.recipientList = [{ recipientType: 'NITEX' }, { recipientType: 'BUYER' }];
		}
		ApiClient.POST(url, postData)
			.then(() => {
				dispatch(fetchDesignToolComment(id));
				generateArtBoardNumber(id);
				setOpenCommentPanel(true);
				toastSuccess('Comment Saved!');
				setLoader(false);
			})
			.catch((err) => {
				toastError(err.response.data.message);
				setLoader(false);
			});
	};

	useEffect(() => {
		const intervalId = setInterval(() => {
			if (exportCanvasData.current) {
				try {
					// Temporarily disable guideline before export
					setShowGuideLine(false);
					setTimeout(() => {
						let canvasExportedData = exportCanvasData.current?.exportCanvas(false);
						onCanvasChange(canvasExportedData);
						// Re-enable guideline after save
						setTimeout(() => setShowGuideLine(true), 100);
					}, 100);
				} catch {
					setShowGuideLine(true);
					/* Empty Block */
				}
			}
		}, 60000);

		return () => clearInterval(intervalId);
	}, []);

	useEffect(() => {
		const intervalId = setInterval(() => {
			timer.current += 10;
			setTimerCount(timer.current);
		}, 10000);

		return () => clearInterval(intervalId);
	}, []);

	const setHasAnyChange = () => {
		setDisabled(false);
	};

	const renderDesignSaveStatus = () => {
		let minutes = Math.floor(timerCount / 60);
		let seconds = timerCount - minutes * 60;
		let timeSeconds = minutes > 0 ? minutes + 'm' : seconds + 's';
		return (
			<li>
				<span className="regular-12 gray_dark_03 cursor-default">{`Saved ${timeSeconds} ago`}</span>
				<button
					className="button text size32 ml-2"
					disabled={disabled && !isErrorOccurred}
					onClick={() => saveAllCanvasData(false, true)}
				>
					Save
				</button>
			</li>
		);
	};

	const handleRightPanel = () => {
		if (openCommentPanel) {
			setOpenCommentPanel(false);
		}
	};

	const onSetActiveTool = (tabName) => {
		if (exportCanvasData.current !== null) {
			exportCanvasData.current.changeDesignToolTabName(tabName);
		}
		setSelectedTool(tabName);
	};

	return (
		<>
			<TopHeader
				activeTab={activeTab}
				productId={id}
				openCommentPanel={openCommentPanel}
				setOpenCommentPanel={setOpenCommentPanel}
				loader={loader}
				setLoader={setLoader}
				saveAllCanvasData={saveAllCanvasData}
				renderDesignSaveStatus={renderDesignSaveStatus}
				stompClient={stompClient}
				onChangeActiveTab={(tab) => onChangeActiveTab(tab)}
				setConvertedImage={setConvertedImage}
			/>
			<div>
				{!reload && (
					<div className="design-page-edit-tool" onClick={handleRightPanel}>
						<NitexEditor
							requiredTools={designToolRequiredTools}
							listOfImages={documentList}
							// listOfAttachments={attachmentList}
							removeDocumentById={removeDocument}
							setHasAnyChange={setHasAnyChange}
							pinnedImages={pinnedImage || []}
							uploadDocument={uploadProduct}
							listOfCanvasData={artBoardData}
							removeArtBoard={removeArtBoard}
							ref={exportCanvasData}
							addNewPost={addNewPost}
							memberList={designToolCommentStore?.memberList}
							commentNumberSerial={commentNumber}
							userInfo={getUserInfo()}
							artBoardWiseImage={convertedImage ? convertedImage : defaultArtBoardImages}
							setActiveToolName={setActiveToolName}
							listOfComments={designToolCommentStore.commentList?.data || []}
							reArrangeCanvasHandler={reArrangeCanvasHandler}
							onSetActiveTab={(tabName) => onSetActiveTool(tabName)}
							disableCanvas={openCommentPanel}
							showGuideLine={showGuideLine}
							guideLinePosition={490}
							guideLineColor="#cc2121"
							guideLineDashPattern={[7, 5]}
						/>
					</div>
				)}
				{TABS_NAME.includes(selectedTool) && (
					<div className="block-suggestions-sidebar scroll-y-label">
						<div className="tab image-file-tab design-tool-tab">
							<ul>
								<li
									className="regular-12 weight-500"
									onClick={() => onSetActiveTool('property')}
								>
									PROPERTY
								</li>
								<li
									className="regular-12 weight-500"
									onClick={() => onSetActiveTool('images')}
								>
									IMAGE
								</li>
								<li
									className={`regular-12 weight-500 ${
										selectedTool === 'files' ? 'active' : ''
									} `}
									onClick={() => onSetActiveTool('files')}
								>
									FILES
								</li>
								<li
									className={`regular-12 weight-500 ${
										selectedTool === 'block' ? 'active' : ''
									} `}
									onClick={() => onSetActiveTool('block')}
								>
									BLOCK SUGGESTION
								</li>
								<li
									className="regular-12 weight-500"
									onClick={() => onSetActiveTool('artboard')}
								>
									ARRANGE ARTBOARD
								</li>
							</ul>
						</div>

						{selectedTool === 'files' && designToolStore?.id && (
							<FileUploads
								isShowArtworkId={true}
								id={designToolStore.id}
								originFrom="FILE"
								onSetActiveTool={onSetActiveTool}
								onPlaceImageOnArtboard={onPlaceImageOnArtboard}
								allowedFileType=".pdf, .xls, .xlsx, .xlsb, .doc, .docx, .psd, .ai, .dxf, .zip, .eps, .csv, .ppt, .pptx, .rar"
							/>
						)}
						{selectedTool === 'block' && <BlockSuggestion fromPage="DESIGNTOOL" />}
					</div>
				)}
			</div>
		</>
	);
};

export default DesignTool;
